import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Sparkles,
  RefreshCw,
  Leaf,
  Ruler,
  BatteryCharging,
  Smartphone,
  ShieldCheck,
  Truck,
  BrainCircuit,
  Heart,
  Wifi,
  HelpCircle,
  Gem,
  Wrench,
} from "lucide-react";

const faqItems = [
  {
    icon: Gem,
    question: "Qu'est-ce qui distingue CynthIA d'un simple capteur d'humidité ?",
    answer:
      "CynthIA est bien plus qu'un simple capteur : c'est un boîtier intelligent qui embarque plusieurs capteurs et agit comme votre expert horticole personnel. Là où un hygromètre classique affiche un chiffre brut, CynthIA croise en permanence trois mesures essentielles — humidité du sol, température et hygrométrie de l'air — avec la météo locale et les besoins spécifiques de plus de 400 espèces de plantes. Le résultat ? Des recommandations claires et personnalisées, adaptées à votre jardin. C'est la différence entre consulter un thermomètre et avoir un médecin à vos côtés.",
  },
  {
    icon: RefreshCw,
    question: "Que comprend mon achat ?",
    answer:
      "Votre achat est un paiement unique, sans abonnement ni frais cachés. Le Pack Solo (89 €) inclut un boîtier CynthIA, l'accès complet à l'Expert Horticole, l'application avec historique et alertes, et 8 piles AA offertes (plus de 400 jours d'autonomie). Le Pack Duo (159 €) inclut deux boîtiers avec gestion multi-zones, 16 piles AA offertes et une économie de plus de 19 € par rapport à deux packs Solo. Dans les deux cas, l'Expert Horticole est inclus à vie — pas d'abonnement à prévoir.",
  },
  {
    icon: Wrench,
    question: "L'installation est-elle accessible à tout le monde ?",
    answer:
      "Absolument. Nous avons conçu CynthIA pour qu'il soit aussi simple à installer qu'à utiliser. Il suffit d'insérer les piles, de planter le boîtier dans la terre, d'ouvrir l'application et de suivre les instructions. En deux minutes, votre jardin est connecté. Aucun câble, aucune configuration technique. Et si vous avez la moindre question, notre équipe vous accompagne avec une réponse garantie en moins de deux heures.",
  },
  {
    icon: Leaf,
    question: "CynthIA est-il adapté à toutes les plantes ?",
    answer:
      "Oui, sans exception. Que vous cultiviez des tomates au potager, des orchidées en intérieur, des arbres fruitiers ou des massifs floraux, CynthIA s'adapte. Notre intelligence artificielle intègre une base de connaissances de plus de 400 espèces, avec les seuils d'humidité, de température et d'hygrométrie optimaux pour chacune. Vous pouvez changer de plante à tout moment dans l'application — CynthIA ajuste instantanément ses recommandations.",
  },
  {
    icon: Ruler,
    question: "Quelle surface un boîtier CynthIA peut-il couvrir ?",
    answer:
      "Un seul boîtier surveille efficacement une zone de culture allant jusqu'à 10 m², soit l'équivalent d'un potager d'une grande terrasse ou d'un balcon généreux. Pour les espaces plus grands ou si vous souhaitez surveiller deux environnements différents — par exemple votre potager et vos plantes d'intérieur — le Pack Jardin Complet (2 boîtiers) couvre jusqu'à 20 m² avec une gestion unifiée dans l'application.",
  },
  {
    icon: BatteryCharging,
    question: "Comment fonctionne l'autonomie de plus de 200 jours ?",
    answer:
      "CynthIA fonctionne avec 4 piles AA standard — Aucune prise de tête ! Pas de batterie à recharger toutes les semaines, pas de câble. Soyez libre de placer votre CynthIA n'importe où ! Avec chaque boîtier, nous vous offrons un lot de 8 piles AA, soit plus de 400 jours d'autonomie totale. Lorsqu'il est temps de les remplacer, l'application vous en informe sereinement. Un simple changement de piles et votre boîtier repart pour un nouveau cycle complet. Des piles AA, vous en trouverez partout — c'est la liberté absolue.",
  },
  {
    icon: Smartphone,
    question: "Sur quels appareils fonctionne l'application ?",
    answer:
      "L'application CynthIA est accessible depuis tout navigateur web moderne — Safari, Chrome, Firefox — sur iPhone, Android, tablette et ordinateur. Conçue comme une application progressive (PWA), elle offre une expérience fluide et élégante sans nécessiter de téléchargement depuis un store. Ajoutez-la simplement à votre écran d'accueil pour y accéder en un geste.",
  },
  {
    icon: BrainCircuit,
    question: "Concrètement, que fait l'Expert Horticole CynthIA ?",
    answer:
      "Votre Expert Horticole CynthIA, propulsé par l'intelligence artificielle Gemini de Google, agit sur trois axes complémentaires. Premièrement, il analyse vos données capteurs en les croisant avec les prévisions météo pour déterminer le moment idéal d'arrosage — ni trop tôt, ni trop tard. Deuxièmement, il diagnostique les problèmes de vos plantes par simple photo : prenez un cliché d'une feuille suspecte et recevez un diagnostic détaillé avec le traitement adapté en quelques secondes. Troisièmement, il répond à toutes vos questions jardinage en tenant compte de vos conditions réelles — pas des conseils génériques, mais un accompagnement sur mesure. CynthIA compile tous les éléments de votre jardin pour vous faciliter la vie et vous faire redécouvrir le jardinage.",
  },
  {
    icon: ShieldCheck,
    question: "Comment sont protégées mes données personnelles ?",
    answer:
      "La protection de vos données est au cœur de notre engagement. Toutes les informations de vos capteurs sont chiffrées de bout en bout (HTTPS/TLS) et hébergées en Europe. L'IA Gemini ne reçoit que des données anonymisées. Nous ne vendons et ne partagerons jamais vos informations personnelles à des tiers. Vous pouvez à tout moment demander l'accès, la modification ou la suppression complète de vos données. CynthIA est intégralement conforme au RGPD.",
  },
  {
    icon: Wifi,
    question: "CynthIA fonctionne-t-il si mon WiFi ne couvre pas bien le jardin ?",
    answer:
      "CynthIA intègre une connexion WiFi optimisé. Si votre téléphone capte un signal, même faible, à l'endroit choisi, CynthIA se connectera. En cas de perte temporaire de connexion, aucune inquiétude : votre application vous en informera.",
  },
  {
    icon: Truck,
    question: "Quels sont les délais et conditions de livraison ?",
    answer:
      "La livraison est offerte sur l'ensemble de nos commandes en France métropolitaine. Votre commande est préparée et expédiée sous 24 heures ouvrées, puis livrée sous 4 à 5 jours ouvrés. Vous choisissez le mode de livraison qui vous convient : à domicile ou en Point Relais (Mondial Relay). Un numéro de suivi vous est envoyé par email dès l'expédition pour suivre votre colis sereinement.",
  },
  {
    icon: Heart,
    question: "Que se passe-t-il si CynthIA ne me convient pas ?",
    answer:
      "Votre satisfaction est notre priorité absolue. Vous disposez de 30 jours — soit le double du délai légal — pour découvrir CynthIA en toute sérénité. Si l'expérience ne répond pas à vos attentes, nous vous remboursons intégralement, sans condition et sans question. Par ailleurs, chaque boîtier est couvert par notre garantie commerciale de 2 ans. Nous croyons profondément en notre produit, c'est pourquoi nous doublons nos garanties.",
  },
];

const FAQSection = () => {
  return (
    <section className="py-20 md:py-28 bg-white border-t border-slate-100" id="faq">
      <div className="container mx-auto px-5 md:px-4 max-w-3xl">
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100 mb-5 border border-emerald-200">
            <HelpCircle className="w-3.5 h-3.5 text-emerald-700 mr-2" />
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-800">Questions fréquentes</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-4">
            Nous répondons à <br className="hidden md:block" />
            <span className="text-emerald-600">toutes vos questions</span>
          </h2>
          <p className="text-lg text-slate-500">
            Transparence, clarté et bienveillance — parce que vous méritez de faire un choix éclairé.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Accordion type="single" collapsible className="space-y-3">
            {faqItems.map((item, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="bg-slate-50 rounded-2xl border border-slate-100 hover:border-emerald-200 transition-colors px-6 data-[state=open]:border-emerald-300 data-[state=open]:bg-emerald-50/30"
              >
                <AccordionTrigger className="hover:no-underline py-5 gap-3">
                  <div className="flex items-center gap-3 text-left">
                    <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <item.icon className="w-4.5 h-4.5 text-emerald-700" />
                    </div>
                    <span className="font-bold text-base md:text-lg text-slate-900">{item.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed text-[15px] pl-12 pr-2 pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <motion.div
          className="mt-12 md:mt-16 text-center bg-slate-50 rounded-2xl p-8 border border-slate-100"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <p className="text-slate-900 font-semibold mb-2">Une question qui n'apparaît pas ici ?</p>
          <p className="text-slate-500 text-sm mb-4">
            Notre équipe se fera un plaisir de vous accompagner personnellement.
          </p>
          <a
            href="mailto:contact@naturalinksmartgarden.fr"
            className="inline-flex items-center gap-2 text-emerald-700 font-bold underline underline-offset-4 hover:text-emerald-800 transition-colors"
          >
            Écrivez-nous — réponse garantie sous 2h
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
